import userManager from '../auth/userManager';

const apiGatewayBase = process.env.REACT_APP_BILLER_API_GATEWAY || '';
let originalFetch: typeof fetch | null = null;

const getValidAccessToken = async (): Promise<string | undefined> => {
  const user = await userManager.getUser();
  if (!user) return undefined;

  if (user.expired) {
    // Silent renew (prompt=none) está roto en el servidor: dispara /home/error 500 en un iframe.
    // No intentamos refresh aquí; devolvemos undefined y dejamos que el 401 dispare el redirect.
    return undefined;
  }

  return user.access_token;
};

const resolveToken = async (providedToken?: string): Promise<string | undefined> => {
  if (providedToken) return providedToken;
  return getValidAccessToken();
};

export const installAuthFetchInterceptor = (): void => {
  if (originalFetch) {
    return;
  }

  originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit & { token?: string; retryOn401?: boolean }) => {
    const requestUrl = input instanceof Request ? input.url : input.toString();
    const requestInput: RequestInfo = input instanceof Request ? input : input.toString();
    const shouldAttachAuth = apiGatewayBase && requestUrl.startsWith(apiGatewayBase);

    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }

    if (shouldAttachAuth && !headers.has('Authorization')) {
      const token = await resolveToken(init?.token);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const runFetch = async () => originalFetch!(requestInput, { ...init, headers });
    let response = await runFetch();

    if (response.status === 401 && shouldAttachAuth && init?.retryOn401 !== false) {
      const refreshedToken = await getValidAccessToken();
      if (refreshedToken) {
        headers.set('Authorization', `Bearer ${refreshedToken}`);
        response = await runFetch();
      }
    }

    return response;
  };
};

export const authFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit & { token?: string; retryOn401?: boolean }
): Promise<Response> => {
  if (!originalFetch) {
    installAuthFetchInterceptor();
  }
  const normalizedInput: RequestInfo = input instanceof URL ? input.toString() : input;
  return globalThis.fetch(normalizedInput, init);
};

export const getAuthorizationHeader = async (token?: string): Promise<Record<string, string>> => {
  const resolved = await resolveToken(token);
  return resolved ? { Authorization: `Bearer ${resolved}` } : {};
};

export default authFetch;
